import type { FileMetadata, ProcessedData } from "@/types/services/storage";
import { logger } from "@/utils/debug";

class IndexedDBService {
  private dbName = "atlasio-store";
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the database - following MDN IndexedDB pattern
   */
  async init(): Promise<void> {
    if (this.db) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      // Use a longer timeout to allow for database deletion and recreation
      const timeout = setTimeout(() => {
        logger.error("[IndexedDB] init: Timeout after 10 seconds");
        this.initPromise = null;
        reject(new Error("Database initialization timed out"));
      }, 10000);

      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        clearTimeout(timeout);
        const error = request.error;
        logger.error("[IndexedDB] init: Error", error);
        this.initPromise = null;
        reject(error || new Error("Failed to open database"));
      };

      request.onupgradeneeded = (event) => {
        logger.log(
          "[IndexedDB] init: onupgradeneeded - creating/updating stores"
        );
        const db = (event.target as IDBOpenDBRequest).result;

        // Always ensure all required stores exist (create if they don't)
        if (!db.objectStoreNames.contains("projects")) {
          const projectStore = db.createObjectStore("projects", {
            keyPath: "id",
          });
          projectStore.createIndex("created_at", "created_at", {
            unique: false,
          });
        }

        if (!db.objectStoreNames.contains("dataflows")) {
          const dataflowStore = db.createObjectStore("dataflows", {
            keyPath: "dataflow_id",
          });
          dataflowStore.createIndex("project_id", "project_id", {
            unique: false,
          });
        }

        if (!db.objectStoreNames.contains("uploaded-files")) {
          const fileStore = db.createObjectStore("uploaded-files", {
            keyPath: "fileId",
          });
          fileStore.createIndex("fileName", "fileName", { unique: false });
        }

        if (!db.objectStoreNames.contains("dataset-cache")) {
          const datasetStore = db.createObjectStore("dataset-cache", {
            keyPath: "dbTableId",
          });
          datasetStore.createIndex("cachedAt", "cachedAt", { unique: false });
        }

        // Create Zustand persistence stores
        if (!db.objectStoreNames.contains("projects-state")) {
          db.createObjectStore("projects-state", { keyPath: "key" });
        }

        if (!db.objectStoreNames.contains("workspaces")) {
          db.createObjectStore("workspaces", { keyPath: "key" });
        }

        if (!db.objectStoreNames.contains("datasets")) {
          db.createObjectStore("datasets", { keyPath: "key" });
        }

        if (!db.objectStoreNames.contains("transformed-data")) {
          db.createObjectStore("transformed-data", { keyPath: "dataflowId" });
        }

        if (!db.objectStoreNames.contains("groups-config")) {
          db.createObjectStore("groups-config", { keyPath: "datasetId" });
        }
      };

      request.onsuccess = () => {
        clearTimeout(timeout);
        this.db = request.result;
        const stores = Array.from(this.db.objectStoreNames);

        const requiredStores = [
          "projects",
          "dataflows",
          "uploaded-files",
          "dataset-cache",
          "projects-state",
          "workspaces",
          "datasets",
          "transformed-data",
          "groups-config",
        ];
        const missingStores = requiredStores.filter((s) => !stores.includes(s));

        if (missingStores.length > 0) {
          logger.warn(
            "[IndexedDB] init: Missing stores detected:",
            missingStores
          );
          logger.warn(
            "[IndexedDB] init: Database exists but stores are missing. Deleting and recreating..."
          );

          // Close the database
          this.db.close();
          this.db = null;
          this.initPromise = null;

          // Delete and recreate to ensure stores are created
          let deleteTimeout: ReturnType<typeof setTimeout> | null = setTimeout(
            () => {
              this.initPromise = null;
              reject(
                new Error(
                  "Database deletion timed out while fixing missing stores."
                )
              );
            },
            5000
          );

          const deleteRequest = indexedDB.deleteDatabase(this.dbName);

          deleteRequest.onsuccess = () => {
            if (deleteTimeout) {
              clearTimeout(deleteTimeout);
              deleteTimeout = null;
            }
            setTimeout(() => {
              this.init().then(resolve).catch(reject);
            }, 100);
          };

          deleteRequest.onerror = () => {
            if (deleteTimeout) {
              clearTimeout(deleteTimeout);
              deleteTimeout = null;
            }
            this.initPromise = null;
            reject(
              new Error("Failed to delete database to fix missing stores.")
            );
          };

          deleteRequest.onblocked = () => {
            logger.warn(
              "[IndexedDB] init: Database deletion blocked - other tabs may have it open"
            );
            if (deleteTimeout) {
              clearTimeout(deleteTimeout);
            }
            deleteTimeout = setTimeout(() => {
              this.initPromise = null;
              reject(
                new Error(
                  "Database deletion is blocked by other tabs. Please close all other tabs and refresh this page."
                )
              );
            }, 15000);
          };
        } else {
          this.initPromise = null;
          resolve();
        }
      };
    });

    return this.initPromise;
  }

  // ==================== PROJECTS ====================

  async storeProject(project: any): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    // Verify store exists
    if (!this.db.objectStoreNames.contains("projects")) {
      throw new Error(
        "Projects store does not exist. Database may not be properly initialized."
      );
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["projects"], "readwrite");
      const store = transaction.objectStore("projects");
      const request = store.put(project);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getProject(projectId: string): Promise<any | null> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["projects"], "readonly");
      const store = transaction.objectStore("projects");
      const request = store.get(projectId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllProjects(): Promise<any[]> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["projects"], "readonly");
      const store = transaction.objectStore("projects");
      const request = store.getAll();

      request.onsuccess = () => {
        const projects = request.result || [];
        // Sort by created_at descending
        projects.sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return dateB - dateA;
        });
        resolve(projects);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["projects"], "readwrite");
      const store = transaction.objectStore("projects");
      const request = store.delete(projectId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== DATAFLOWS (WORKSPACES) ====================

  async storeDataflow(dataflow: any): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    // Verify store exists
    if (!this.db.objectStoreNames.contains("dataflows")) {
      throw new Error(
        "Dataflows store does not exist. Database may not be properly initialized."
      );
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataflows"], "readwrite");
      const store = transaction.objectStore("dataflows");
      const request = store.put(dataflow);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getDataflow(dataflowId: string): Promise<any | null> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataflows"], "readonly");
      const store = transaction.objectStore("dataflows");
      const request = store.get(dataflowId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getDataflowsByProject(projectId: string): Promise<any[]> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataflows"], "readonly");
      const store = transaction.objectStore("dataflows");
      const index = store.index("project_id");
      const request = index.getAll(projectId);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteDataflow(dataflowId: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataflows"], "readwrite");
      const store = transaction.objectStore("dataflows");
      const request = store.delete(dataflowId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteDataflowsByProject(projectId: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataflows"], "readwrite");
      const store = transaction.objectStore("dataflows");
      const index = store.index("project_id");
      const request = index.openCursor(IDBKeyRange.only(projectId));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== UPLOADED FILES ====================

  async storeFileData(
    fileId: string,
    data: any[],
    metadata: FileMetadata
  ): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["uploaded-files"], "readwrite");
      const store = transaction.objectStore("uploaded-files");
      const request = store.put({ fileId, data, metadata });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getFileData(
    fileId: string
  ): Promise<{ data: any[]; metadata: FileMetadata } | null> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["uploaded-files"], "readonly");
      const store = transaction.objectStore("uploaded-files");
      const request = store.get(fileId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllFiles(): Promise<{ fileId: string; metadata: FileMetadata }[]> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["uploaded-files"], "readonly");
      const store = transaction.objectStore("uploaded-files");
      const request = store.getAll();

      request.onsuccess = () => {
        const files = request.result || [];
        // Return only fileId and metadata (not the full data)
        resolve(
          files.map((file: any) => ({
            fileId: file.fileId,
            metadata: file.metadata,
          }))
        );
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteFileData(fileId: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["uploaded-files"], "readwrite");
      const store = transaction.objectStore("uploaded-files");
      const request = store.delete(fileId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async listFileIds(): Promise<string[]> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["uploaded-files"], "readonly");
      const store = transaction.objectStore("uploaded-files");
      const request = store.getAllKeys();

      request.onsuccess = () => resolve(request.result as string[]);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== DATASET CACHE ====================

  async storeDatasetData(
    dbTableId: string,
    data: any[],
    metadata: FileMetadata
  ): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataset-cache"], "readwrite");
      const store = transaction.objectStore("dataset-cache");
      const request = store.put({
        dbTableId,
        data,
        metadata,
        cachedAt: new Date().toISOString(),
      });

      request.onsuccess = () => {
        logger.log(
          `[IndexedDB] Successfully cached dataset ${dbTableId} (${data.length} rows)`
        );
        resolve();
      };
      request.onerror = () => {
        logger.error(
          `[IndexedDB] Failed to cache dataset ${dbTableId}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  async getDatasetData(
    dbTableId: string
  ): Promise<{ data: any[]; metadata: FileMetadata } | null> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataset-cache"], "readonly");
      const store = transaction.objectStore("dataset-cache");
      const request = store.get(dbTableId);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          logger.log(
            `[IndexedDB] Loaded cached dataset ${dbTableId} (${result.data.length} rows)`
          );
          resolve({
            data: result.data,
            metadata: result.metadata,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async isDatasetCached(dbTableId: string): Promise<boolean> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["dataset-cache"], "readonly");
      const store = transaction.objectStore("dataset-cache");
      const request = store.get(dbTableId);

      request.onsuccess = () => resolve(!!request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== TRANSFORMED DATA ====================

  async storeTransformedData(
    dataflowId: string,
    processedData: ProcessedData
  ): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        ["transformed-data"],
        "readwrite"
      );
      const store = transaction.objectStore("transformed-data");

      // Store the data with dataflowId as the key
      // This includes all geocoded lat/lng values and will persist across page reloads
      const dataToStore = {
        dataflowId,
        data: processedData.data, // Includes geocoded coordinates
        metadata: processedData.metadata, // Includes latitude/longitude columns
      };

      const request = store.put(dataToStore);

      request.onsuccess = () => {
        logger.log(
          `[IndexedDB] Successfully stored transformed data for ${dataflowId} (${processedData.data.length} rows)`
        );
        resolve();
      };
      request.onerror = () => {
        logger.error(
          `[IndexedDB] Failed to store transformed data for ${dataflowId}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  async getTransformedData(dataflowId: string): Promise<ProcessedData | null> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        ["transformed-data"],
        "readonly"
      );
      const store = transaction.objectStore("transformed-data");
      const request = store.get(dataflowId);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // Verify that geocoded coordinates are present if latitude/longitude columns exist
          const hasLatColumn = result.metadata?.columns?.some((col: string) =>
            /^latitude$/i.test(col)
          );
          const hasLonColumn = result.metadata?.columns?.some((col: string) =>
            /^longitude$/i.test(col)
          );

          if (hasLatColumn && hasLonColumn && result.data?.length > 0) {
            const latCol = result.metadata.columns.find((col: string) =>
              /^latitude$/i.test(col)
            );
            const lonCol = result.metadata.columns.find((col: string) =>
              /^longitude$/i.test(col)
            );
            const hasGeocodedData = result.data.some(
              (row: any) =>
                (row[latCol!] != null && row[latCol!] !== "") ||
                (row[lonCol!] != null && row[lonCol!] !== "")
            );

            if (hasGeocodedData) {
              logger.log(
                `[IndexedDB] Loaded transformed data with geocoded coordinates for ${dataflowId} (${result.data.length} rows)`
              );
            }
          }

          resolve({
            data: result.data,
            metadata: result.metadata,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteTransformedData(dataflowId: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        ["transformed-data"],
        "readwrite"
      );
      const store = transaction.objectStore("transformed-data");
      const request = store.delete(dataflowId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== GROUPS CONFIGURATION ====================

  /**
   * Stores groups configuration for a dataset
   * @param datasetId - Unique identifier for the dataset (file_id for user_file, db_table_id for data_input)
   * @param config - Groups configuration object
   */
  async storeGroupsConfig(
    datasetId: string,
    config: {
      groupColumnName: string | null;
      visibleGroups: string[];
      groupConfigs: Record<
        string,
        {
          name?: string;
          color?: string;
          shape?: "circle" | "square" | "triangle" | "star" | "diamond";
        }
      >;
      layerConfig?: { color?: string; shape?: "circle" | "square" | "triangle" | "star" | "diamond" };
    }
  ): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    // Verify store exists
    if (!this.db.objectStoreNames.contains("groups-config")) {
      throw new Error(
        "Groups config store does not exist. Database may not be properly initialized."
      );
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["groups-config"], "readwrite");
      const store = transaction.objectStore("groups-config");
      const request = store.put({
        datasetId,
        ...config,
        updatedAt: new Date().toISOString(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieves groups configuration for a dataset
   * @param datasetId - Unique identifier for the dataset (file_id for user_file, db_table_id for data_input)
   * @returns Groups configuration or null if not found
   */
  async getGroupsConfig(datasetId: string): Promise<{
    groupColumnName: string | null;
    visibleGroups: string[];
    groupConfigs: Record<
      string,
      {
        name?: string;
        color?: string;
        shape?: "circle" | "square" | "triangle" | "star";
      }
    >;
    layerConfig?: { color?: string; shape?: "circle" | "square" | "triangle" | "star" | "diamond" };
  } | null> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["groups-config"], "readonly");
      const store = transaction.objectStore("groups-config");
      const request = store.get(datasetId);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            groupColumnName: result.groupColumnName ?? null,
            visibleGroups: result.visibleGroups ?? [],
            groupConfigs: result.groupConfigs ?? {},
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== STORAGE QUOTA ====================

  async getIndexedDBQuota(): Promise<{
    usage: number;
    quota: number;
    usagePercent: number;
  }> {
    if (!navigator.storage || !navigator.storage.estimate) {
      return { usage: 0, quota: 0, usagePercent: 0 };
    }

    try {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        usagePercent:
          estimate.quota && estimate.usage
            ? (estimate.usage / estimate.quota) * 100
            : 0,
      };
    } catch (error) {
      logger.error("Failed to get storage quota:", error);
      return { usage: 0, quota: 0, usagePercent: 0 };
    }
  }
}

export const indexedDBService = new IndexedDBService();
